/**
 * The job catalog, on the wire (ADR-0044, PR 2 Task 7).
 *
 * PR 1 put the catalog in the backend (`workers/jobCatalog.ts`) because it
 * carries zod schemas and the `defaultActive` predicates, neither of which can
 * cross a request. The Team page needs the other half of each template: the
 * sentence, its blanks, the ceiling, and whether a second line of the kind is
 * allowed. That half is a wire contract, so it is declared here and the backend
 * template is typed against it.
 *
 * Copy rules apply to every string that reaches a surface from here: no
 * em-dashes, never "job" in customer copy, and the kind id is an internal
 * identifier that never renders.
 */
/**
 * Which picker a blank in a kind's sentence opens.
 *
 * Presentation, declared beside the slot so no surface ever keys a control off
 * a kind id. The list is closed: a kind whose blank needs a control that is not
 * here is a platform change, not a catalog edit.
 */
export const JOB_SLOT_PICKERS = [
    "schedule",
    "interval",
    "repos",
    "paths",
    /** GitHub logins, typed one per chip: the authors a line names or excludes. */
    "authors",
    "backlogView",
    "lane",
    "people",
    /**
     * Whose tracker tickets a line reads: Team members by user id, plus the
     * literal `unassigned` for tickets with nobody on them. Empty means anyone.
     */
    "assignees",
    "channel",
    "environment",
    "number",
    "time",
    "rule",
    "text",
    /** A closed set of named answers, listed on the slot as `choices`. */
    "choice",
    /** The checks a review line applies: a roster with switches, from the specialist catalog. */
    "checks",
    /**
     * A blank that is not a choice: it prints its `emptyLabel` as text and
     * writes nothing.
     *
     * A kind whose blank is bound to a filter the runtime fills for itself still
     * has to say what it uses, and offering a control there would let a person
     * silently switch the line off. The catalog says so once, here, instead of
     * a surface keying that off a kind id.
     */
    "fixed",
];
/**
 * The events whose subject is one pull request.
 *
 * A derivation over the closed vocabulary, not an addition to it. It exists so
 * that "which lines can I ask for a run of, and what do I have to name?" is
 * answered from declared data rather than from a list of kind ids that would
 * go stale the first time the catalog grew.
 *
 * `qa.failed` and `review.posted` are deliberately absent even though a pull
 * request is nearby: their subject is the TICKET moving, which is why a fix
 * round is not something a person asks for on a PR.
 */
export const JOB_PULL_REQUEST_EVENTS = [
    "pr.opened",
    "pr.updated",
    "pr.ready",
    "pr.approved_and_verified",
    "pr.signals_settled",
    "review.changes_requested",
];
/**
 * Is this kind's run about one pull request?
 *
 * True when it runs on events and EVERY event it listens for names a pull
 * request. "Every" rather than "any" on purpose: `builder.fix_round` listens
 * for `review.changes_requested` and `qa.failed`, so a person asking for it
 * would have to name a pull request for one half of its job and a ticket for
 * the other, which is not one ask.
 */
export function jobSubjectIsPullRequest(kind) {
    if (kind.triggerType !== "event")
        return false;
    if (kind.listens.length === 0)
        return false;
    return kind.listens.every((event) => JOB_PULL_REQUEST_EVENTS.includes(event));
}
// ---------------------------------------------------------------------------
// The pipeline stages (the Add-a-Team page and the Team page)
// ---------------------------------------------------------------------------
/**
 * A stage of the pipeline, in the order a ticket walks it.
 *
 * Both the create page and the Team page group lines by stage rather than by
 * Agent: what a manager wants to read is "who proposes, who plans, who
 * reviews", and an Agent-first grouping splits a craft whose lines sit in two
 * stages (Reeve reads proposals AND plans) across the page. The order and the
 * map below are declared once, here, or the two surfaces print the same
 * pipeline differently.
 *
 * `anytime` is the catch-all: the kinds no stage owns, and any kind a newer
 * server invented. It prints last and carries no hint.
 */
export const JOB_STAGE_IDS = [
    "proposed",
    "plan",
    "build",
    "review",
    "qa",
    "done",
    "anytime",
];
export const JOB_STAGES = [
    { id: "proposed", label: "Proposed", hint: "what is next" },
    { id: "plan", label: "Plan", hint: "before any code" },
    { id: "build", label: "Build", hint: "to a pull request" },
    { id: "review", label: "Review", hint: "every pull request" },
    { id: "qa", label: "QA", hint: "in the repository environment" },
    { id: "done", label: "Done", hint: "a person merges" },
    { id: "anytime", label: "Any time", hint: null },
];
/**
 * Which stage each kind belongs to. Exhaustive over `JOB_KIND_IDS` by type, so
 * a new kind cannot ship unplaced: the alternative is a kind that quietly
 * sinks to the bottom of every page under "Any time".
 */
export const JOB_STAGE_OF_KIND = {
    "pm.propose": "proposed",
    "pm.take_assigned": "proposed",
    "staff.plan": "plan",
    "builder.build": "build",
    "builder.fix_round": "build",
    "reviewer.review_pr": "review",
    "qa.verify_pr": "qa",
    "qa.sign_off_release": "qa",
    "builder.merge": "done",
    "chronicler.brief": "anytime",
    "chronicler.work_log": "anytime",
    "chronicler.deploy_notes": "anytime",
    "chronicler.custom": "anytime",
    "pm.route_question": "anytime",
};
/**
 * The stage this kind renders under. A kind the map does not know reads as
 * `anytime` rather than throwing: a surface rendering a line a newer server
 * wrote should print it somewhere, not blank the page.
 */
export function jobStageOf(kind) {
    return JOB_STAGE_OF_KIND[kind] ?? "anytime";
}
//# sourceMappingURL=jobCatalog.js.map